(function ($) {
  // ---------------------------------------------------------------------------
  // Consent gated Spotify episode embeds.
  //
  // Mirrors assets/src/js/lib/video.js: the facade (thumbnail + play shell) is
  // always rendered because the page HTML is cached before consent is known,
  // and no Spotify player exists until the visitor has granted "marketing"
  // consent. The player is mounted through the official Spotify iFrame API
  // (https://open.spotify.com/embed/iframe-api/v1), which is only loaded once
  // consent is granted — never before, so no third party is contacted
  // pre-consent.
  //
  // Three surfaces can occupy the facade box; they are mutually exclusive:
  //
  //   pre-consent   `.is-gated`    the consent layer with WPConsent's accept
  //                                button (rendered in the page HTML but hidden
  //                                by CSS until this file marks it, so a page
  //                                loaded by an already-consented visitor never
  //                                flashes the button)
  //   consented     `.is-ready`    the play disc
  //   playing       `.is-playing`  the mounted native player
  //
  // Playback flow (once consent is granted):
  //
  //   1. consent → the API script loads and a controller is created for each
  //      embed BEFORE `.is-ready` is set, so the custom play is only enabled
  //      once a controller actually exists.
  //   2. clicking the custom play (a real user gesture) shows the native
  //      player (`.is-playing`) and calls `controller.play()` synchronously,
  //      still inside the click handler, so the browser treats it as
  //      gesture-driven playback.
  //   3. Spotify does not accept an `autoplay=1` query param, so no autoplay
  //      parameter is ever added to the embed URL.
  //
  // If the API cannot load, or `controller.play()` is rejected by browser
  // policy, the native player and its own controls are still shown so the
  // visitor can start playback there.
  //
  // Revoking consent destroys the controller and unloads the player, then
  // restores the gated state.
  //
  // Focus chain (keyboard):
  //
  //   accept button → Enter → media ("Play episode")   [grant hands focus over]
  //   media         → Enter → player iframe → Tab → the player's own controls
  //
  // Pointer activation never moves focus.
  //
  // Accept button / consent cookie: the plugin also has a delegated document
  // handler for `.wpconsent-iframe-accept-button` that saves only the single
  // category named on the button — `{essential, marketing}` here. That object
  // does not cover every entry in `wpconsent.slugs`, so on the next pageview
  // `processBannerDisplay()` reads it as outdated, expires the cookie, brings
  // the privacy banner back and leaves analytics locked even though the visitor
  // just accepted. So this file binds a listener at target phase on OUR gate
  // button only (inside `.podcast__consent`, never the plugin's own
  // placeholders) and:
  //
  //   feature-detect at click time — `window.WPConsent.savePreferences` plus an
  //   array `window.wpconsent.slugs`. Present → `stopPropagation()` first (the
  //   plugin listens at document, in bubble phase, so its partial save never
  //   runs: one write, not two), then hand `savePreferences()` a COMPLETE
  //   preferences object built by `buildStatsOnlyPrefs()`: `{essential: true}`
  //   plus every slug in `wpconsent.slugs` — `marketing` true, `marketing`
  //   false, and each service term resolved through `wpconsent.wpca_services`
  //   (true only when its category is `marketing`, otherwise false, unknown
  //   service → false) — with `marketing: true` guaranteed at the end, because
  //   marketing is the category this gate accepts.
  //
  //   Absent → do NOT stop propagation: the plugin's stock handler saves its
  //   partial object, which degrades to today's behaviour. This file never
  //   writes a consent cookie or calls `wp_set_consent` itself — every write
  //   stays inside the plugin's `savePreferences()` (cookie write, banner hide,
  //   script/iframe unlock, `wp_set_consent`, `wpconsent_consent_saved`), so
  //   the consent event chain that drives `evaluate()` below is unchanged.
  // ---------------------------------------------------------------------------
  var CONSENT_CATEGORY = "marketing";
  var SPOTIFY_API_SRC = "https://open.spotify.com/embed/iframe-api/v1";

  // One EmbedController per `.podcast[data-platform="spotify"]` element.
  // `pending` guards against creating a second controller while the API is
  // still loading.
  var controllers = new WeakMap();
  var pending = new WeakSet();
  var apiPromise = null;

  function hasConsent() {
    if (typeof window.wp_has_consent !== "function") {
      return false;
    }

    try {
      return !!window.wp_has_consent(CONSENT_CATEGORY);
    } catch (e) {
      return false;
    }
  }

  // Shapes the object the plugin's savePreferences() writes — it never writes
  // anything itself. The gate accepts "marketing", so the recorded choice has
  // to name every slug in wpconsent.slugs (the plugin expires a cookie whose
  // keys don't, and re-shows the banner on the next pageview). Service terms
  // are resolved via wpca_services because manual_toggle_services makes the
  // service key — not the category — decide whether that service unlocks, so
  // marketing services must be explicitly true for analytics to run later.
  function buildStatsOnlyPrefs() {
    var prefs = { essential: true };
    var slugs = window.wpconsent.slugs;
    var services = Array.isArray(window.wpconsent.wpca_services)
      ? window.wpconsent.wpca_services
      : [];

    slugs.forEach(function (slug) {
      if (slug === "essential") {
        return;
      }

      if (slug === CONSENT_CATEGORY) {
        prefs[slug] = true;
        return;
      }

      if (slug === "marketing") {
        prefs[slug] = false;
        return;
      }

      // Anything else is a service term: look up its category and only unlock
      // the services that belong to marketing (unknown service → false).
      var category = null;

      for (var i = 0; i < services.length; i++) {
        if (services[i] && services[i].slug === slug) {
          category = services[i].category;
          break;
        }
      }

      prefs[slug] = category === CONSENT_CATEGORY;
    });

    // The category this button accepts, whatever the slug list contained.
    prefs[CONSENT_CATEGORY] = true;

    return prefs;
  }

  // Target-phase listener on our own gate button. The plugin's partial-save
  // handler sits at document (bubble phase), so stopping propagation here
  // keeps it from running after us — no double write — while a missing API
  // falls through untouched to that stock handler. Detection runs at click
  // time, so a plugin script that loads after this bundle still counts.
  function bindGateAccept(button) {
    button.addEventListener("click", function (e) {
      var api = window.WPConsent;
      var config = window.wpconsent;

      if (
        !api ||
        typeof api.savePreferences !== "function" ||
        !config ||
        !Array.isArray(config.slugs)
      ) {
        // No API: let the plugin's own document handler run (partial save =
        // current behaviour). We never write a consent cookie ourselves.
        return;
      }

      e.stopPropagation();
      api.savePreferences(buildStatsOnlyPrefs());
    });
  }

  // Loads the official Spotify iFrame API exactly once, and only once this is
  // called (i.e. after consent). The API announces itself through a single
  // global hook; any hook that was already installed is chained so nothing
  // else on the page is clobbered.
  function loadSpotifyApi() {
    if (apiPromise) {
      return apiPromise;
    }

    apiPromise = new Promise(function (resolve, reject) {
      var settled = false;
      var previous = window.onSpotifyIframeApiReady;

      window.onSpotifyIframeApiReady = function (IFrameAPI) {
        if (typeof previous === "function") {
          try {
            previous(IFrameAPI);
          } catch (e) {
            // An unrelated callback must not stop our promise from resolving.
          }
        }

        if (settled) {
          return;
        }

        settled = true;
        resolve(IFrameAPI);
      };

      var script = document.createElement("script");
      script.src = SPOTIFY_API_SRC;
      script.async = true;
      script.onerror = function () {
        if (settled) {
          return;
        }

        settled = true;
        reject(new Error("Spotify iFrame API failed to load"));
      };

      document.head.appendChild(script);
    });

    return apiPromise;
  }

  function spotifyEmbeds() {
    return $('.podcast[data-platform="spotify"]');
  }

  // The player frame. `.podcast__frame` starts as an empty <div> host, but the
  // Spotify iFrame API replaces that element with its own bare iframe, so the
  // same class ends up on whichever element is the live frame (the API iframe
  // after JS adopts it, or the host <div> wrapping the fallback iframe).
  function getFrame(embed) {
    return embed.querySelector(".podcast__frame");
  }

  // Returns the empty <div class="podcast__frame"> host that createController
  // needs, recreating it when a previous destroy removed it (the API removes the
  // element it was handed when the player is torn down).
  function ensureHost(embed) {
    var frame = getFrame(embed);

    if (frame && frame.tagName === "DIV") {
      return frame;
    }

    var host = document.createElement("div");
    host.className = "podcast__frame";
    embed.appendChild(host);

    return host;
  }

  // After createController the API swaps the host <div> for a bare iframe that
  // carries no class. Adopt it as the frame (adding `.podcast__frame`) so the
  // facade CSS can keep it hidden behind the thumbnail until `.is-playing` and
  // size it to the 352px stage.
  function adoptPlayerFrame(embed) {
    var frame = embed.querySelector("iframe");

    if (!frame) {
      return null;
    }

    if (!frame.classList.contains("podcast__frame")) {
      var parent = frame.parentNode;

      // Host-wraps-iframe case: a `.podcast__frame` parent already styles it via
      // the `&__frame iframe` rule, so only the bare-iframe (API replacement)
      // case needs the class moved onto the iframe itself.
      if (
        !parent ||
        !parent.classList ||
        !parent.classList.contains("podcast__frame")
      ) {
        frame.classList.add("podcast__frame");
      }
    }

    frame.setAttribute("title", "Spotify player");

    return frame;
  }

  // The `spotify:episode:<id>` URI the API needs. Falls back to deriving it
  // from the canonical embed URL so the markup stays the single source.
  function getUri(embed) {
    var uri = embed.getAttribute("data-spotify-uri");

    if (uri) {
      return uri;
    }

    var src = embed.getAttribute("data-src") || "";
    var match = src.match(/\/embed\/([a-z]+)\/([A-Za-z0-9]+)/i);

    if (match) {
      return "spotify:" + match[1] + ":" + match[2];
    }

    return "";
  }

  function destroyController(controller) {
    if (controller && typeof controller.destroy === "function") {
      try {
        controller.destroy();
      } catch (e) {
        // Already torn down.
      }
    }
  }

  // Page HTML is cached, so a page rendered before the facade became a <button>
  // may still carry a plain <div> as its play control. Mirror the button
  // semantics in place. Consent state itself is never touched here.
  function upgradeMedia(embed) {
    var media = $(embed).find(".podcast__media")[0];

    if (!media || media.tagName === "BUTTON") {
      return;
    }

    media.setAttribute("role", "button");
    media.setAttribute("tabindex", "0");
    media.setAttribute("aria-label", "Play episode");
  }

  // Activating a control hides it (play swaps the facade for the player, a
  // grant swaps the consent layer for the play disc, a revoke swaps back),
  // which drops focus to <body> and loses the visitor's place: the next Tab
  // restarts at the top of the document. So after keyboard activation the
  // focus moves to whichever surface is visible now.
  //
  // Pointer activation is deliberately left alone — it only changes where Tab
  // would continue from, and moving focus after a click can flash a ring the
  // visitor never asked for. `:focus-visible` distinguishes the two for native
  // buttons and for the legacy <div> path alike.
  function wasKeyboard(el) {
    try {
      return el.matches(":focus-visible");
    } catch (e) {
      // No :focus-visible support: prefer the accessible behaviour.
      return true;
    }
  }

  function focusOrFail(el) {
    // focus() on a control that ended up display:none is a silent no-op.
    if (el && typeof el.focus === "function") {
      el.focus();
    }
  }

  function focusPlayer(embed) {
    var frame = getFrame(embed);

    if (frame && frame.tagName === "IFRAME") {
      focusOrFail(frame);
      return;
    }

    // Host element: focus the mounted iframe inside it.
    focusOrFail(frame ? frame.querySelector("iframe") : null);
  }

  // Injects the plain native embed when the API is unavailable. Spotify's own
  // controls then let the visitor start playback.
  function ensureFallbackFrame(embed) {
    // Already mounted through the API: nothing to inject.
    if (embed.querySelector("iframe")) {
      return;
    }

    var src = embed.getAttribute("data-src") || "";

    if (!src) {
      return;
    }

    // Append into the `.podcast__frame` host so the `&__frame iframe` rule
    // styles and hides it; it is never a bare direct child of the stage.
    var host = ensureHost(embed);

    if (!host) {
      return;
    }

    var frame = document.createElement("iframe");
    frame.setAttribute("src", src);
    frame.setAttribute("title", "Spotify player");
    frame.setAttribute("width", "100%");
    frame.setAttribute("height", "352");
    frame.setAttribute("frameborder", "0");
    frame.setAttribute(
      "allow",
      "autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture",
    );
    frame.setAttribute("allowfullscreen", "");
    frame.setAttribute("loading", "lazy");

    host.appendChild(frame);
  }

  // Creates the controller (which mounts the player) and only then enables the
  // custom play. Called on consent and on consent-type-defined.
  function initPlayer(embed) {
    if (!hasConsent() || controllers.has(embed) || pending.has(embed)) {
      return;
    }

    var host = ensureHost(embed);
    var uri = getUri(embed);

    // Nothing to mount: enable the play disc so the native fallback can be
    // injected on click.
    if (!host || !uri) {
      $(embed).addClass("is-ready");
      return;
    }

    pending.add(embed);

    loadSpotifyApi()
      .then(function (IFrameAPI) {
        // Consent may have been revoked while the API was loading.
        if (!hasConsent()) {
          pending.delete(embed);
          return;
        }

        try {
          IFrameAPI.createController(
            host,
            { uri: uri, width: "100%", height: "352" },
            function (controller) {
              pending.delete(embed);

              if (!hasConsent()) {
                destroyController(controller);
                return;
              }

              // The API replaces the host <div> with its own bare iframe;
              // adopt it so the facade CSS controls (hides and sizes) it.
              adoptPlayerFrame(embed);

              controllers.set(embed, controller);

              // The controller exists before the custom play is enabled, so
              // controller.play() can run synchronously in the click handler.
              $(embed).addClass("is-ready");
            },
          );
        } catch (e) {
          pending.delete(embed);

          if (hasConsent()) {
            $(embed).addClass("is-ready");
          }
        }
      })
      .catch(function () {
        pending.delete(embed);

        // API unavailable: enable the custom play; clicking it injects the
        // plain native embed so the visitor can still start playback.
        if (hasConsent()) {
          $(embed).addClass("is-ready");
        }
      });
  }

  function stop(embed) {
    var controller = controllers.get(embed);

    if (controller) {
      destroyController(controller);
      controllers.delete(embed);
    }

    pending.delete(embed);

    // destroy() removes the generated iframe (which carries `.podcast__frame`).
    // Drop any frame element still around (for example the fallback iframe),
    // then reinsert an empty host so a later consent cycle can mount again.
    var frame = getFrame(embed);

    if (frame && frame.parentNode) {
      frame.parentNode.removeChild(frame);
    }

    var stray = embed.querySelector("iframe");

    if (stray && stray.parentNode) {
      stray.parentNode.removeChild(stray);
    }

    ensureHost(embed);

    $(embed).removeClass("is-playing").removeClass("is-ready");
  }

  // Play is only reachable once a controller exists (`.is-ready`), so this runs
  // inside the real click/keyboard gesture.
  function play(embed) {
    var controller = controllers.get(embed);

    // No controller (API unavailable or still loading): fall back to the plain
    // native embed, which carries Spotify's own controls.
    if (!controller) {
      ensureFallbackFrame(embed);
    }

    // Show the native player and hide the external facade. This DOM change is
    // synchronous, in the same gesture as the play call below.
    $(embed).addClass("is-playing");

    if (controller && typeof controller.play === "function") {
      try {
        var result = controller.play();

        if (result && typeof result.catch === "function") {
          // A policy rejection leaves the native controls on screen.
          result.catch(function () {});
        }
      } catch (e) {
        // Same: the native player is already visible.
      }
    }
  }

  // Single source of truth for the two facade states. The consent decision is
  // never written into the cached HTML: `.is-gated` is added (or removed) from
  // here only, which is what keeps an already-consented page load free of the
  // accept button while a missing consent API — which reads as "no consent" —
  // still ends with the button on screen.
  function evaluate() {
    var granted = hasConsent();
    var active = document.activeElement;
    var refocus = null;

    // The consent layer is what holds focus when a grant lands (clicking or
    // keyboard-activating its accept button is how consent gets granted), and
    // hiding it would strand focus on <body>.
    if (
      granted &&
      active &&
      active.closest &&
      active.closest(".podcast__consent")
    ) {
      refocus = active.closest('.podcast[data-platform="spotify"]');
    }

    spotifyEmbeds().toggleClass("is-gated", !granted);

    if (granted) {
      // Mount the player (this loads the Spotify iFrame API) and enable the
      // custom play once each controller exists.
      spotifyEmbeds().each(function () {
        initPlayer(this);
      });
    }

    if (refocus) {
      // The play disc has just appeared where the accept button was — the next
      // step of the same two-step flow.
      focusOrFail(refocus.querySelector(".podcast__media"));
    }
  }

  function revoke() {
    var active = document.activeElement;
    var refocus = null;

    // Keyboard play leaves focus inside the player; the revoke tears that
    // player down, so pick the surface to hand focus to before anything hides.
    spotifyEmbeds().each(function () {
      var frame = getFrame(this);

      if (
        frame &&
        active &&
        (active === frame || frame.contains(active)) &&
        wasKeyboard(active)
      ) {
        refocus = this;
      }
    });

    spotifyEmbeds()
      .removeClass("is-ready")
      .addClass("is-gated")
      .each(function () {
        stop(this);
      });

    if (refocus) {
      // The player is gone; the consent layer is the live surface again.
      focusOrFail(
        refocus.querySelector(".podcast__consent button") ||
          refocus.querySelector(".podcast__media"),
      );
    }
  }

  // Delegated so dynamically inserted sections keep working.
  document.addEventListener("click", function (e) {
    var target = e.target;
    if (!target || !target.closest) {
      return;
    }

    // The consent layer is a sibling of the media control, so its accept button
    // never resolves to `.podcast__media`: the plugin owns that click and this
    // handler stays out of its way (no second handler, no play).
    var media = target.closest(".podcast__media");
    if (!media) {
      return;
    }

    var embed = media.closest('.podcast[data-platform="spotify"]');
    var $embed = $(embed);

    // No play affordance before consent is granted.
    if (
      !embed ||
      !$embed.hasClass("is-ready") ||
      $embed.hasClass("is-playing")
    ) {
      return;
    }

    var keyboardPlay = wasKeyboard(media);
    play(embed);

    if (keyboardPlay) {
      // The facade is display:none now, so focusing it would silently do
      // nothing and focus would fall to <body>. The player is the live surface.
      focusPlayer(embed);
    }
  });

  // Enter/Space for facade markup that predates the <button> element. Native
  // buttons are skipped so the browser's own activation is never doubled.
  document.addEventListener("keydown", function (e) {
    if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") {
      return;
    }

    var media =
      e.target && e.target.closest
        ? e.target.closest(".podcast__media")
        : null;
    if (!media || media.tagName === "BUTTON") {
      return;
    }

    // Scoped to Spotify: an Acast embed's media button must not reach here.
    if (!media.closest('.podcast[data-platform="spotify"]')) {
      return;
    }

    e.preventDefault();
    media.click();
  });

  document.addEventListener("wp_listen_for_consent_change", function (e) {
    if (!e.detail || !e.detail[CONSENT_CATEGORY]) {
      return;
    }

    if (e.detail[CONSENT_CATEGORY] === "allow") {
      evaluate();
    } else if (e.detail[CONSENT_CATEGORY] === "deny") {
      revoke();
    }
  });

  document.addEventListener("wp_consent_type_defined", function () {
    evaluate();
  });

  $(() => {
    spotifyEmbeds().each(function () {
      upgradeMedia(this);

      // Our gate's accept button only — scoped by the selector, so the
      // plugin's placeholder buttons elsewhere on the page keep their own
      // handler untouched.
      var accept = this.querySelector(
        ".podcast__consent .wpconsent-iframe-accept-button",
      );

      if (accept) {
        bindGateAccept(accept);
      }
    });

    evaluate();

    // Safety net for a consent script that loads after the app bundle.
    if (typeof window.wp_has_consent !== "function") {
      setTimeout(evaluate, 2000);
    }
  });
})(jQuery);
